import {useEffect, useMemo, useState} from "react";
import {TEmailTemplate, TEmailTemplateComponentType, TEmailTemplateType, TComponentPayload} from "../../interfaces/email";
import {getEmailTemplatePreview, getOperationEmailTemplateByType} from "../../services/AdminService";
import {LocalComponent} from "./EmailTemplateComponentBlock";
import {contrastTextColor} from "./emailColors";

export const ASSET_BASE = (process.env.REACT_APP_API_HOST ?? '').replace(/\/?$/, '/');
const BRAND_FONT = "'Mulish', Arial, Helvetica, sans-serif";
const FONT_IMPORT = `<style>@import url('https://fonts.googleapis.com/css2?family=Mulish:wght@300;400;600;700;900&display=swap');body,table,td,div,p,span,a,h1,h2,h3,strong{font-family:${BRAND_FONT};}</style>`;

const DEFAULT_BUTTON_LABEL = "Me connecter";

const withButtonFallback = (label?: string): string =>
  label === undefined ? '' : (label.trim() || DEFAULT_BUTTON_LABEL);

const withBrandFont = (html: string): string =>
  html.includes('</head>')
    ? html.replace('</head>', `${FONT_IMPORT}</head>`)
    : FONT_IMPORT + html;

const escapeHtml = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const safeUrl = (url: string | null | undefined): string => {
  if (!url) return "#";
  const trimmed = url.trim();
  // N'autorise que http(s) et les ancres — bloque javascript:, data:, etc.
  if (/^(https?:\/\/|#)/i.test(trimmed)) return escapeHtml(trimmed);
  return "#";
};

const PREVIEW_VARIABLE_SAMPLES: Record<string, string> = {
  "user.firstName": "Jean",
  "user.lastName": "Dupont",
  "user.civility": "M.",
  "user.points": "250",
  "operationName": "Challenge",
  "companyName": "Joozy",

};

const substitutePreviewVariables = (html: string): string =>
  html.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_match, expr: string) => {
    if (PREVIEW_VARIABLE_SAMPLES[expr]) return PREVIEW_VARIABLE_SAMPLES[expr];
    if (expr.startsWith("user.attributes.")) return "Valeur exemple";
    return "Exemple";
  });

function renderFooterHtml(comp: LocalComponent, shopUrl?: string): string {
  const shop = escapeHtml(shopUrl ?? '#');
  return '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr><td style="background-color:#EEE7F9;padding:24px 24px 0 24px;">'
    + '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr>'
    + `<td width="33.33%" align="center" valign="top" style="padding:8px;font-family:${BRAND_FONT};"><img src="${ASSET_BASE}templateemail/livraison.png" width="48" height="48" alt="" style="display:block;margin:0 auto 8px;border:0;" /><div style="font-size:14px;line-height:16px;">Livraison</div></td>`
    + `<td width="33.33%" align="center" valign="top" style="padding:8px;font-family:${BRAND_FONT};"><img src="${ASSET_BASE}templateemail/securite.png" width="48" height="48" alt="" style="display:block;margin:0 auto 8px;border:0;" /><div style="font-size:14px;line-height:16px;">Site sécurisé</div></td>`
    + `<td width="33.33%" align="center" valign="top" style="padding:8px;font-family:${BRAND_FONT};"><img src="${ASSET_BASE}templateemail/sav.png" width="48" height="48" alt="" style="display:block;margin:0 auto 8px;border:0;" /><div style="font-size:14px;line-height:16px;">Sav garantie</div></td>`
    + '</tr></table>'
    + '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;margin:30px 0;"><tr>'
    + `<td width="30%" align="left" valign="middle" style="padding:8px;"><img src="${ASSET_BASE}templateemail/logoJoozy.png" width="120" alt="Logo" style="display:block;border:0;max-width:120px;height:auto;" /></td>`
    + `<td width="70%" align="right" valign="middle" style="padding-right:30px;"><a href="${shop}" style="font-size:14px;color:#000000;text-decoration:none;font-weight:700;">Accéder à mon espace bénéficiaire ➔</a></td>`
    + '</tr></table>'
    + '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;margin-top:16px;"><tr>'
    + `<td align="center" style="padding:8px;font-family:${BRAND_FONT};font-size:12px;line-height:18px;">${escapeHtml(comp.contenu ?? 'Pour toutes questions, contactez-nous au : 02 52 35 04 60 • contact@hellojoozy.fr')}</td>`
    + '</tr></table>'
    + '</td></tr></table>';
}

const PREVIEW_BODY_STYLE = "font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:16px;text-align:center;";

function wrapPreview(inner: string): string {
  return substitutePreviewVariables(`<html><body style="${PREVIEW_BODY_STYLE}">${inner}</body></html>`);
}

// Rester synchronisé avec EmailComponentRenderer::render() (api/src/Common/Utils).
// Toute modif du HTML d'un composant doit être répercutée des deux côtés.
function renderComponentsHtml(components: LocalComponent[], primaryColor = '#6725D2', shopUrl?: string, operationLogoUrl?: string): string {
  return [...components]
    .sort((a, b) => a.order - b.order)
    .map(comp => {
      let imgSrc = '';
      switch (comp.type) {
      case TEmailTemplateComponentType.TEXT:
        return comp.contenu ? `<div style="padding:0 30px;">${comp.contenu}</div>` : '';
      case TEmailTemplateComponentType.BUTTON: {
        const buttonUrl = comp.urlType === 'shop' ? safeUrl(shopUrl) : safeUrl(comp.url);
        const buttonLabel = comp.label || DEFAULT_BUTTON_LABEL;
        let buttonConfig: Record<string, string> = {};
        try { buttonConfig = JSON.parse(comp.contenu ?? '{}'); } catch { /* ignore */ }
        const buttonBgColor = buttonConfig.backgroundColor || primaryColor;
        return `<div style="text-align:center;margin:16px 0;"><a href="${buttonUrl}" style="display:inline-block;padding:12px 24px;background-color:${escapeHtml(buttonBgColor)};color:${contrastTextColor(buttonBgColor)};text-decoration:none;border-radius:4px;">${escapeHtml(buttonLabel)}</a></div>`;
      }
      case TEmailTemplateComponentType.BANNER: {
        if (!comp.img) return '';
        imgSrc = comp.img.match(/^https?:\/\//) ? comp.img : `${ASSET_BASE}/${comp.img}`;
        const bannerUrl = comp.urlType === 'shop' ? safeUrl(shopUrl) : safeUrl(comp.url);
        const hasBannerLink = bannerUrl !== '#';
        const bannerImgTag = `<img src="${escapeHtml(imgSrc)}" alt="${escapeHtml(comp.alt ?? "")}" style="max-width:100%;height:auto;display:block;margin:0 auto;" />`;
        return `<div style="text-align:center;">${hasBannerLink ? `<a href="${bannerUrl}" target="_blank">${bannerImgTag}</a>` : bannerImgTag}</div>`;
      }
      case TEmailTemplateComponentType.FOOTER:
        return renderFooterHtml(comp, shopUrl);
      case TEmailTemplateComponentType.HEADER: {
        let config: Record<string, string> = {};
        try { config = JSON.parse(comp.contenu ?? '{}'); } catch { /* ignore */ }
        const rawLogoUrl = config.logoUrl;
        const isDefaultLiberty = rawLogoUrl === 'templateemail/headerJoozy.png' || rawLogoUrl === `${ASSET_BASE}templateemail/headerJoozy.png`;
        const effectiveLogoUrl = rawLogoUrl && !isDefaultLiberty ? rawLogoUrl : (operationLogoUrl || `${ASSET_BASE}logoJoozyBlack.png`);
        const logoSrc = effectiveLogoUrl.match(/^https?:\/\//) ? effectiveLogoUrl : `${ASSET_BASE}/${effectiveLogoUrl}`;
        const bgColor = config.backgroundColor || primaryColor;
        const opacity = config.opacity !== undefined ? parseFloat(config.opacity) : 0.3;
        const gradientColor = config.gradientEndColor || '#FFFFFF';

        const hexToRgba = (hex: string, alpha: number): string => {
          const h = hex.replace('#', '');
          const r = parseInt(h.substring(0, 2), 16);
          const g = parseInt(h.substring(2, 4), 16);
          const b = parseInt(h.substring(4, 6), 16);
          return `rgba(${r},${g},${b},${alpha.toFixed(2)})`;
        };

        const effectiveBgColor = opacity < 1.0 ? hexToRgba(bgColor, opacity) : bgColor;
        const bgStyle = `background:linear-gradient(180deg,${escapeHtml(effectiveBgColor)} 0%,${escapeHtml(gradientColor)} 100%)`;

        return `<div style="${bgStyle};padding:40px 20px;text-align:center;">
          <img src="${escapeHtml(logoSrc)}" alt="Logo" style="display:block;margin:0 auto;border:0;max-width:200px;height:auto;" />
        </div>`;
      }
      default:
        return '';
      }
    })
    .join('');
}

type UseEmailPreviewOptions = {
  template: TEmailTemplate | null;
  operationId: string | undefined;
  components: LocalComponent[];
  contentOverride?: string | null;
  buttonLabel?: string;
  primaryColor?: string;
  shopUrl?: string,
  logoUrl?: string,
};

type PreviewStatus = "loading" | "ready" | "error";
type UseEmailPreviewResult = { previewHtml: string | null; status: PreviewStatus };


export function useEmailPreview({template, operationId, components, contentOverride, buttonLabel, primaryColor, shopUrl, logoUrl}: UseEmailPreviewOptions): UseEmailPreviewResult {
  const [shellHtml, setShellHtml] = useState<string | null>(null);
  const [status, setStatus] = useState<PreviewStatus>("loading");
  const [debouncedComponents, setDebouncedComponents] = useState(components);
  const [debouncedContent, setDebouncedContent] = useState(contentOverride);
  const [debouncedButtonLabel, setDebouncedButtonLabel] = useState(withButtonFallback(buttonLabel));

  const fetchShell = (id: string): void => {
    getEmailTemplatePreview(id, undefined, {content: "%%TEMPLATE_CONTENT%%", buttonLabel: '%%BUTTON_LABEL%%'})
      .then(res => { setShellHtml(res.html); setStatus("ready"); })
      .catch(() => {
        setStatus("error");
      });
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedComponents(components), 800);
    return (): void => clearTimeout(timer);
  }, [components]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedContent(contentOverride), 800);
    return (): void => clearTimeout(timer);
  }, [contentOverride]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedButtonLabel(withButtonFallback(buttonLabel)), 800);
    return (): void => clearTimeout(timer);
  }, [buttonLabel]);

  useEffect(() => {
    if (template?.id) {
      fetchShell(template.id);
    } else if (operationId) {
      getOperationEmailTemplateByType(operationId, TEmailTemplateType.CUSTOM_TEMPLATE)
        .then(existing => { existing?.id ? fetchShell(existing.id) : setStatus("ready"); })
        .catch(() => setStatus("ready"));
    } else {
      setStatus("ready");
    }
  }, []);

  const previewHtml = useMemo(() => {
    if (status === "loading") return null;
    const componentsHtml = renderComponentsHtml(debouncedComponents, primaryColor, shopUrl, logoUrl);
    const htmlToInject = debouncedContent != null
      ? debouncedContent + componentsHtml
      : componentsHtml;

    if (shellHtml?.includes('%%TEMPLATE_CONTENT%%')) {
      const html = shellHtml
        .replace('%%TEMPLATE_CONTENT%%', () => htmlToInject)
        .replace('%%BUTTON_LABEL%%', () => debouncedButtonLabel);
      return withBrandFont(substitutePreviewVariables(html));
    }

    if (!htmlToInject) return null;
    return wrapPreview(htmlToInject);
  }, [shellHtml, debouncedContent, debouncedComponents, debouncedButtonLabel, primaryColor, shopUrl, status]);


  return {previewHtml, status};
}

export function buildLocalPreviewHtml(components: TComponentPayload[]): string | null {
  const componentsHtml = renderComponentsHtml(components as LocalComponent[]);
  if (!componentsHtml) return null;
  return wrapPreview(componentsHtml);
}
